import { SARSidebarDefinition } from 'app/modules/sidebar/models';
import { IconArrowRight, IconBuildingBank } from '@u21/tabler-icons';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import {
  REPORT_TYPE_PATH_MAP,
  ReportType,
} from 'app/modules/filings/constants';
import { SarStatus } from 'app/modules/fincenSarOld/models';
import { useGetFinCENSAR } from 'app/modules/fincenSAR/queries/useGetFinCENSAR';
import {
  U21Chip,
  U21Subsection,
  U21Spacer,
  U21LoadError,
} from 'app/shared/u21-ui/components';
import { SidebarDataRow } from 'app/modules/sidebar/components/utils/SidebarDataRow';
import { StatusPill } from 'app/modules/fincenSAR/components/StatusPill';
import { formatDate, formatDatetime } from 'app/shared/utils/date';
import { SidebarCommentsCollapsible } from 'app/modules/sidebar/components/SidebarCommentsCollapsible';
import { SidebarAssociatedEntities } from 'app/modules/sidebar/components/SidebarAssociatedEntities';
import { SidebarAssociatedTransactions } from 'app/modules/sidebar/components/SidebarAssociatedTransactions';
import { SidebarAssociatedAlerts } from 'app/modules/sidebar/components/SidebarAssociatedAlerts';
import { SidebarAssociatedFilings } from 'app/modules/sidebar/components/SidebarAssociatedFilings';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { STATUS_TO_COLOR } from 'app/modules/investigations/constants';
import { useSelector } from 'react-redux';
import {
  selectAgent,
  selectHasReadAssignmentsPermissions,
  selectHasReadCasesPermissions,
} from 'app/modules/session/selectors';
import { isQueueAccessible } from 'app/modules/queues/utils';
import { selectDLEnabledForSARDetail } from 'app/shared/featureFlags/selectors';
import { selectClassifiedQueuesEnabled } from 'app/modules/orgSettings/selectors';
import { useMemo } from 'react';
import { sortCreatedAt } from 'app/modules/sidebar/utils';
import { ArticleDeadlineChip } from 'app/modules/deadlines/components/ArticleDeadlineChip';
import { useQueryClient } from '@tanstack/react-query';
import { FINCEN_SAR_QUERY_KEYS } from 'app/modules/fincenSAR/queries/keys';
import { DEFAULT_GET_TRANSACTIONS_RESPONSE } from 'app/modules/transactions/constants';
import { useGetTransactions } from 'app/modules/transactions/queries/useGetTransactions';
import { useGetEntities } from 'app/modules/entities/queries/useGetEntities';
import { DEFAULT_GET_ENTITIES_RESPONSE } from 'app/modules/entities/constants';
import { DEFAULT_OFFSET } from 'app/shared/pagination/constants';
import { isColdStorageLoading } from 'app/shared/utils/fastArchHelpers';

interface Props {
  data: SARSidebarDefinition['data'];
}

export const SidebarFinCENSAR = (props: Props) => {
  const {
    data: { id, hideActionButton },
  } = props;

  const hasReadCasesPermission = useSelector(selectHasReadCasesPermissions);
  const hasReadAssignmentsPermission = useSelector(
    selectHasReadAssignmentsPermissions,
  );
  const classifiedQueuesEnabled = useSelector(selectClassifiedQueuesEnabled);
  const sessionAgent = useSelector(selectAgent);
  const useSARCacheEndpoint = useSelector(selectDLEnabledForSARDetail);

  const queryClient = useQueryClient();
  const {
    query: { data: sar, isError, isLoading, refetch },
    coldStorageLoading,
  } = useGetFinCENSAR(id);

  const {
    data: {
      txns: sarTransactions,
      count: sarTransactionsCount,
    } = DEFAULT_GET_TRANSACTIONS_RESPONSE,
    isLoading: isLoadingTransactions,
  } = useGetTransactions(
    {
      sar_ids: [id],
      limit: 100,
      offset: DEFAULT_OFFSET,
      sort_column: 'event_time',
      sort_direction: 'descending',
    },
    {
      enabled: id > 0,
    },
  );

  const {
    data: {
      entities: sarEntities,
      count: sarEntitiesCount,
    } = DEFAULT_GET_ENTITIES_RESPONSE,
    isLoading: isLoadingEntities,
  } = useGetEntities(
    {
      sar_ids: [id],
      limit: 100,
      offset: DEFAULT_OFFSET,
      sort_column: 'created_at',
      sort_direction: 'descending',
    },
    {
      enabled: id > 0,
    },
  );

  const loadingEntities =
    isLoadingEntities || isColdStorageLoading(coldStorageLoading.entity);
  const loadingTransactions =
    isLoadingTransactions || isColdStorageLoading(coldStorageLoading.txn_event);

  const sortedAlerts = useMemo(() => {
    if (!sar) {
      return [];
    }
    return sortCreatedAt(sar.alerts);
  }, [sar]);

  const sortedFilings = useMemo(() => {
    if (!sar?.sars) {
      return [];
    }
    return sortCreatedAt(sar.sars);
  }, [sar]);

  return (
    <U21SideMenu
      actionButtonProps={
        hideActionButton
          ? undefined
          : {
              children: 'Go to Filing Page',
              endIcon: <IconArrowRight />,
              to: REPORT_TYPE_PATH_MAP[ReportType.FINCEN_SAR].replace(
                ':id',
                String(id),
              ),
            }
      }
      loading={isLoading}
      noPadding
      title="FinCEN SAR Details"
    >
      {(() => {
        // loading handled by U21SideMenu
        if (isLoading) {
          return null;
        }

        if (isError) {
          return (
            <U21LoadError label="FinCEN SAR" onTryAgain={() => refetch()} />
          );
        }

        if (!sar) {
          return null;
        }

        return (
          <U21Spacer spacing={0.5}>
            <U21Subsection
              disableTitleCase
              shadedHeader
              title={sar.name}
              titleIcon={<IconBuildingBank />}
            >
              <U21Spacer>
                <SidebarDataRow label="Assigned to" showEmptyFields>
                  {sar.reviewer?.full_name || sar.reviewer?.email}
                </SidebarDataRow>
                <SidebarDataRow label="Queue" showEmptyFields>
                  {sar.queue?.title}
                </SidebarDataRow>
                <SidebarDataRow label="Status" showEmptyFields>
                  <StatusPill status={sar.status} />
                </SidebarDataRow>
                <SidebarDataRow label="Deadline" showEmptyFields>
                  {sar.status !== SarStatus.ARCHIVED && !sar.submitted && (
                    <ArticleDeadlineChip
                      articleId={id}
                      deadline={sar.deadline}
                      deadlineConfig={sar.deadline_config}
                      onEditSuccess={() => {
                        queryClient.invalidateQueries({
                          queryKey: FINCEN_SAR_QUERY_KEYS.getFinCENSAR(
                            id,
                            useSARCacheEndpoint,
                          ),
                        });
                      }}
                    />
                  )}
                </SidebarDataRow>
                <SidebarDataRow label="Case" showEmptyFields>
                  {sar.case && (
                    <U21Chip
                      color={STATUS_TO_COLOR[sar.case.status]}
                      to={
                        hasReadCasesPermission &&
                        (!classifiedQueuesEnabled ||
                          isQueueAccessible(
                            hasReadAssignmentsPermission,
                            sessionAgent.accessible_queues,
                            sar.case.queue_id,
                            sar.case.queue_access_type,
                          ))
                          ? ROUTES_MAP.casesId.path.replace(
                              ':id',
                              String(sar.case.id),
                            )
                          : undefined
                      }
                      variant={sar.case.status === 'OPEN' ? 'filled' : 'ghost'}
                    >
                      {sar.case.title}
                    </U21Chip>
                  )}
                </SidebarDataRow>
                <SidebarDataRow label="Created at" showEmptyFields>
                  {formatDatetime(sar.created_at)}
                </SidebarDataRow>
                <SidebarDataRow label="Filed at" showEmptyFields>
                  {formatDate(sar.filed_at, undefined, {
                    showTimezone: true,
                    timezone: 'America/New_York',
                  })}
                </SidebarDataRow>
                <SidebarDataRow label="BSA tracking ID" showEmptyFields>
                  {sar.bsa_tracking_id}
                </SidebarDataRow>
                <SidebarDataRow label="BSA ID" showEmptyFields>
                  {sar.bsa_id}
                </SidebarDataRow>
                {sar.car_alert_scheduled_at && (
                  <SidebarDataRow
                    label="CAR alert scheduled at"
                    showEmptyFields
                  >
                    {formatDate(sar.car_alert_scheduled_at, undefined, {
                      showTimezone: true,
                      timezone: 'America/New_York',
                    })}
                  </SidebarDataRow>
                )}
                {sar.car_case_scheduled_at && (
                  <SidebarDataRow label="CAR case scheduled at" showEmptyFields>
                    {formatDate(sar.car_case_scheduled_at, undefined, {
                      showTimezone: true,
                      timezone: 'America/New_York',
                    })}
                  </SidebarDataRow>
                )}
              </U21Spacer>
            </U21Subsection>
            <SidebarAssociatedAlerts
              alerts={sortedAlerts}
              count={sortedAlerts.length}
            />
            <SidebarAssociatedFilings
              count={sortedFilings.length}
              filings={sortedFilings}
            />
            <SidebarAssociatedEntities
              loading={loadingEntities}
              count={sarEntitiesCount}
              entities={sarEntities}
              coldStorageLoadingStatus={coldStorageLoading.entity}
            />
            <SidebarAssociatedTransactions
              loading={loadingTransactions}
              count={sarTransactionsCount}
              transactions={sarTransactions}
              coldStorageLoadingStatus={coldStorageLoading.txn_event}
            />
            <SidebarCommentsCollapsible comments={sar.comments} />
          </U21Spacer>
        );
      })()}
    </U21SideMenu>
  );
};
